import { addDays, eachDayOfInterval, endOfWeek, format, startOfMonth, startOfWeek } from 'date-fns'
import type { Appointment } from '@/lib/api'
import {
  COMPRESSED_NIGHT_HEIGHT,
  DAY_START_HOUR,
  MINUTES_PER_SLOT,
  NIGHT_END_HOUR,
  NIGHT_START_HOUR,
  SLOT_HEIGHT,
} from './constants'

const DAYS_IN_MONTH_GRID = 42

const MINUTES_PER_DAY = 24 * 60
const NORMAL_DAY_START = DAY_START_HOUR * 60 // 08:00
const NORMAL_DAY_END = NIGHT_START_HOUR * 60 // 22:00
const NORMAL_SLOT_COUNT = (NORMAL_DAY_END - NORMAL_DAY_START) / MINUTES_PER_SLOT
const NORMAL_DAY_HEIGHT = NORMAL_SLOT_COUNT * SLOT_HEIGHT
const COMPRESSED_NIGHT_START = NIGHT_START_HOUR * 60 // 22:00
const COMPRESSED_NIGHT_END = (NIGHT_END_HOUR + 24) * 60 // 次日 08:00
const COMPRESSED_NIGHT_MINUTES = COMPRESSED_NIGHT_END - COMPRESSED_NIGHT_START // 600

export function parseTime(time: string): number {
  const [h, m] = time.split(':').map(Number)
  return h * 60 + m
}

export function formatTime(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

export function toDateKey(epochSeconds: number): string {
  return format(new Date(epochSeconds * 1000), 'yyyy-MM-dd')
}

/**
 * 课程开始时间是否已过（本地时间口径）。
 * scheduledDate 存的是本地日期的 UTC 零点，startTime 是本地 HH:mm，
 * 取本地日期部分 + 时间部分即为用户感知的开始时刻（与服务端 markNoShow 同口径）。
 */
export function isPastStart(appt: Appointment): boolean {
  const d = new Date(appt.scheduledDate * 1000)
  const [h, m] = appt.startTime.split(':').map(Number)
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), h || 0, m || 0).getTime() <= Date.now()
}

export function getCalendarRange(date: Date, view: 'month' | 'week' | 'day') {
  if (view === 'day') {
    const s = format(date, 'yyyy-MM-dd')
    return { start: s, end: s }
  }
  if (view === 'week') {
    const start = startOfWeek(date, { weekStartsOn: 1 })
    const end = endOfWeek(date, { weekStartsOn: 1 })
    return { start: format(start, 'yyyy-MM-dd'), end: format(end, 'yyyy-MM-dd') }
  }
  const start = startOfWeek(startOfMonth(date), { weekStartsOn: 1 })
  const end = addDays(start, DAYS_IN_MONTH_GRID - 1)
  return { start: format(start, 'yyyy-MM-dd'), end: format(end, 'yyyy-MM-dd') }
}

export function groupByDay(items: Appointment[]): Map<string, Appointment[]> {
  const map = new Map<string, Appointment[]>()
  for (const item of items) {
    const key = toDateKey(item.scheduledDate)
    const list = map.get(key) || []
    list.push(item)
    map.set(key, list)
  }
  for (const list of map.values()) {
    list.sort((a, b) => parseTime(a.startTime) - parseTime(b.startTime))
  }
  return map
}

export interface PositionedAppointment extends Appointment {
  col: number
  colCount: number
  top: number
  height: number
}

function overlaps(a: PositionedAppointment, b: PositionedAppointment): boolean {
  return a.top < b.top + b.height && b.top < a.top + a.height
}

export function assignColumns(items: Appointment[]): PositionedAppointment[] {
  const sorted = [...items].sort((a, b) => parseTime(a.startTime) - parseTime(b.startTime))
  const result: PositionedAppointment[] = []
  let active: PositionedAppointment[] = []

  for (const item of sorted) {
    const top = parseTime(item.startTime)
    const height = parseTime(item.endTime) - top
    active = active.filter((a) => a.top + a.height > top)
    const used = new Set(active.map((a) => a.col))
    let col = 0
    while (used.has(col)) col++
    const positioned: PositionedAppointment = { ...item, col, colCount: 1, top, height }
    active.push(positioned)
    result.push(positioned)
  }

  return result.map((r) => ({
    ...r,
    colCount: Math.max(
      ...result.filter((x) => overlaps(x, r)).map((x) => x.col + 1),
      1,
    ),
  }))
}

export function getDaysInRange(start: Date, end: Date): Date[] {
  return eachDayOfInterval({ start, end })
}

export function getMonthGridDays(date: Date): Date[] {
  const start = startOfWeek(startOfMonth(date), { weekStartsOn: 1 })
  const end = addDays(start, DAYS_IN_MONTH_GRID - 1)
  return eachDayOfInterval({ start, end })
}

export function addCalendarDays(date: Date, amount: number): Date {
  return addDays(date, amount)
}

/** 将一天内的分钟数映射到时间轴像素位置。compressed 为 true 时 22:00-08:00 被压缩为一格。 */
export function minutesToGridY(minutes: number, compressed = false): number {
  if (!compressed) return (minutes / MINUTES_PER_SLOT) * SLOT_HEIGHT
  const m = ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY
  if (m >= NORMAL_DAY_START && m < NORMAL_DAY_END) {
    return ((m - NORMAL_DAY_START) / MINUTES_PER_SLOT) * SLOT_HEIGHT
  }
  const nightMinutes = m < NORMAL_DAY_START ? m + MINUTES_PER_DAY : m
  return (
    NORMAL_DAY_HEIGHT +
    ((nightMinutes - COMPRESSED_NIGHT_START) / COMPRESSED_NIGHT_MINUTES) * COMPRESSED_NIGHT_HEIGHT
  )
}

/** 将时间轴像素位置映射为一天内的分钟数。compressed 为 true 时压缩行统一返回 22:00。 */
export function gridYToMinutes(y: number, compressed = false): number {
  if (!compressed) return Math.floor(y / SLOT_HEIGHT) * MINUTES_PER_SLOT
  if (y < NORMAL_DAY_HEIGHT) {
    return NORMAL_DAY_START + Math.floor(y / SLOT_HEIGHT) * MINUTES_PER_SLOT
  }
  return COMPRESSED_NIGHT_START
}
